import { apiConfig } from '../config/apiConfig';

const AUTH_TOKEN_KEY = 'stm_auth_tokens';
const AUTH_EMAIL_KEY = 'stm_auth_email';
const AUTH_STATE_KEY = 'stm_auth_state';
const AUTH_VERIFIER_KEY = 'stm_auth_verifier';
const AUTH_RETURN_KEY = 'stm_auth_return_path';
const TOKEN_SKEW_MS = 60 * 1000;

const defaultScopes = ['openid', 'email', 'profile'];

const base64UrlEncode = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const generateRandomString = (length = 64) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return Array.from(array).map((x) => chars[x % chars.length]).join('');
};

const generateCodeChallenge = async (verifier) => {
  const data = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
};

const getAuthConfig = () => apiConfig?.auth || {};

const isPlaceholder = (value) =>
  !value || value.includes('YOUR-') || value.includes('TODO');

export const isAuthConfigured = () => {
  const config = getAuthConfig();
  return (
    config?.domain &&
    config?.clientId &&
    config?.redirectSignIn &&
    config?.redirectSignOut &&
    !isPlaceholder(config.domain) &&
    !isPlaceholder(config.clientId)
  );
};

export const isEmailAuthConfigured = () => {
  const config = getAuthConfig();
  return Boolean(
    config?.emailStart &&
      config?.emailVerify &&
      !isPlaceholder(config.emailStart) &&
      !isPlaceholder(config.emailVerify)
  );
};

export const getAuthReturnPath = () => sessionStorage.getItem(AUTH_RETURN_KEY);

export const clearAuthReturnPath = () => sessionStorage.removeItem(AUTH_RETURN_KEY);

const storeTokens = (tokenResponse, existingRefreshToken) => {
  if (!tokenResponse) {
    return;
  }

  const expiresIn = Number(tokenResponse.expires_in || 0) * 1000;
  const expiresAt = Date.now() + (Number.isFinite(expiresIn) ? expiresIn : 0);

  const tokens = {
    accessToken: tokenResponse.access_token,
    idToken: tokenResponse.id_token,
    refreshToken: tokenResponse.refresh_token || existingRefreshToken,
    expiresAt
  };

  localStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(tokens));
};

const getStoredTokens = () => {
  const raw = localStorage.getItem(AUTH_TOKEN_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (error) {
    console.warn('Failed to parse auth tokens:', error);
    return null;
  }
};

const clearTokens = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
};

const isTokenValid = (tokens) => {
  if (!tokens?.accessToken || !tokens?.expiresAt) {
    return false;
  }
  return tokens.expiresAt - TOKEN_SKEW_MS > Date.now();
};

export const isAuthenticated = () => {
  const tokens = getStoredTokens();
  return Boolean(tokens?.refreshToken || isTokenValid(tokens));
};

export const getAccessToken = () => {
  const tokens = getStoredTokens();
  if (!isTokenValid(tokens)) {
    clearTokens();
    return null;
  }
  return tokens.accessToken;
};

export const getIdToken = () => {
  const tokens = getStoredTokens();
  if (!isTokenValid(tokens)) {
    clearTokens();
    return null;
  }
  return tokens.idToken || null;
};

export const getAuthorizationHeader = () => {
  const token = getIdToken() || getAccessToken();
  if (!token) {
    return null;
  }
  return `Bearer ${token}`;
};

export const decodeJwtPayload = (token) => {
  if (!token) {
    return null;
  }
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }
  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(payload);
    return JSON.parse(json);
  } catch (error) {
    console.warn('Failed to decode JWT payload:', error);
    return null;
  }
};

export const getAuthUser = () => {
  const tokens = getStoredTokens();
  const payload = decodeJwtPayload(tokens?.idToken);
  if (!payload) {
    return null;
  }
  return {
    sub: payload.sub,
    email: payload.email,
    emailVerified: payload.email_verified
  };
};

export const getAuthUserId = () => {
  const user = getAuthUser();
  return user?.sub || null;
};

export const getAuthUserEmail = () => {
  const user = getAuthUser();
  return user?.email || null;
};

export const startLogin = async ({ provider, returnPath } = {}) => {
  if (!isAuthConfigured()) {
    throw new Error('Auth not configured');
  }

  const config = getAuthConfig();
  const verifier = generateRandomString(64);
  const challenge = await generateCodeChallenge(verifier);
  const state = generateRandomString(32);

  sessionStorage.setItem(AUTH_STATE_KEY, state);
  sessionStorage.setItem(AUTH_VERIFIER_KEY, verifier);
  if (returnPath) {
    sessionStorage.setItem(AUTH_RETURN_KEY, returnPath);
  }

  const scopeValue = Array.isArray(config.scopes)
    ? config.scopes.join(' ')
    : config.scopes || defaultScopes.join(' ');

  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: scopeValue,
    redirect_uri: config.redirectSignIn,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256'
  });

  if (provider) {
    params.set('identity_provider', provider);
  }

  const url = `${config.domain}/oauth2/authorize?${params.toString()}`;
  window.location.assign(url);
};

export const handleAuthCallback = async () => {
  const config = getAuthConfig();
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  const errorDescription = params.get('error_description');

  if (error) {
    throw new Error(errorDescription || error);
  }

  const code = params.get('code');
  const state = params.get('state');
  const expectedState = sessionStorage.getItem(AUTH_STATE_KEY);
  const verifier = sessionStorage.getItem(AUTH_VERIFIER_KEY);

  if (!code || !state || !expectedState || !verifier || state !== expectedState) {
    throw new Error('Invalid auth callback state');
  }

  const tokenResponse = await exchangeCodeForTokens({
    code,
    verifier,
    redirectUri: config.redirectSignIn,
    clientId: config.clientId,
    domain: config.domain
  });

  sessionStorage.removeItem(AUTH_STATE_KEY);
  sessionStorage.removeItem(AUTH_VERIFIER_KEY);

  storeTokens(tokenResponse);
  return tokenResponse;
};

export const getStoredEmail = () => localStorage.getItem(AUTH_EMAIL_KEY) || '';

export const setStoredEmail = (email) => {
  if (!email) {
    localStorage.removeItem(AUTH_EMAIL_KEY);
    return;
  }
  localStorage.setItem(AUTH_EMAIL_KEY, email);
};

export const startEmailAuth = async (email) => {
  const config = getAuthConfig();
  const endpoint = config?.emailStart;
  if (!endpoint) {
    throw new Error('Email auth not configured');
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  });

  if (!response.ok) {
    const text = await response.text();
    let message = text;
    try {
      const data = JSON.parse(text);
      message = data.error || data.message || text;
    } catch (error) {
      // Ignore JSON parse error and fall back to text.
    }
    throw new Error(message || 'Failed to send code');
  }

  return response.json();
};

export const verifyEmailAuth = async ({ email, code, session }) => {
  const config = getAuthConfig();
  const endpoint = config?.emailVerify;
  if (!endpoint) {
    throw new Error('Email auth not configured');
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, code, session })
  });

  if (!response.ok) {
    const text = await response.text();
    let message = text;
    try {
      const data = JSON.parse(text);
      message = data.error || data.message || text;
    } catch (error) {
      // Ignore JSON parse error and fall back to text.
    }
    throw new Error(message || 'Failed to verify code');
  }

  const tokenResponse = await response.json();
  const existing = getStoredTokens();
  storeTokens(tokenResponse, existing?.refreshToken);
  return tokenResponse;
};

export const refreshTokensIfNeeded = async () => {
  const tokens = getStoredTokens();
  if (!tokens?.refreshToken) {
    return tokens;
  }
  if (isTokenValid(tokens)) {
    return tokens;
  }

  const config = getAuthConfig();
  if (!config?.domain || !config?.clientId) {
    return tokens;
  }
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: config?.clientId || '',
    refresh_token: tokens.refreshToken
  });

  const response = await fetch(`${config?.domain}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body
  });

  if (!response.ok) {
    clearTokens();
    const text = await response.text();
    throw new Error(text || 'Failed to refresh tokens');
  }

  const refreshed = await response.json();
  storeTokens(refreshed, tokens.refreshToken);
  return getStoredTokens();
};

export const logout = () => {
  const config = getAuthConfig();
  clearTokens();

  if (!isAuthConfigured()) {
    return;
  }

  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.redirectSignOut
  });

  window.location.assign(`${config.domain}/logout?${params.toString()}`);
};

const exchangeCodeForTokens = async ({ code, verifier, redirectUri, clientId, domain }) => {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: verifier
  });

  const response = await fetch(`${domain}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to exchange auth code');
  }

  return response.json();
};
